import gql from 'graphql-tag';
import { PassportRecord, ScopeKey } from 'src/types';
import { PassportFormData, ScopeLabelFormValues } from './formTabs/types';

export const UPSERT_PASSPORT_MUTATION = gql`
  mutation UpsertPassport($object: passport_insert_input!) {
    insert_passport_one(
      object: $object
      on_conflict: { constraint: pk_passport, update_columns: [name, description] }
    ) {
      id
    }
  }
`;

export const DELETE_PASSPORT_MUTATION = gql`
  mutation DeletePassport($id: uuid!) {
    update_passport_subscription_set_participant(where: { passport_id: { _eq: $id } }, _set: { deleted_at: "now()" }) {
      affected_rows
    }

    update_passport_subscription_set_scope(where: { passport_id: { _eq: $id } }, _set: { deleted_at: "now()" }) {
      affected_rows
    }

    update_passport_scope_label(where: { passport_id: { _eq: $id } }, _set: { deleted_at: "now()" }) {
      affected_rows
    }

    update_passport_by_pk(pk_columns: { id: $id }, _set: { deleted_at: "now()" }) {
      id
    }
  }
`;

const buildScopeLabelsData = (values: PassportFormData, passport?: PassportRecord) => {
  const existingLabelsMap = new Map((passport?.passport_scope_labels ?? []).map((l) => [l.scope_key, l]));
  const data: Record<string, unknown>[] = [];

  for (const [scopeKey, label] of Object.entries(values.scopeLabels) as [ScopeKey, ScopeLabelFormValues][]) {
    const hasValue = label?.displayLabel || label?.displayDescription;
    const existing = existingLabelsMap.get(scopeKey);

    // Skip if nothing to do: no value and no existing row, or values unchanged
    if (
      (!hasValue && !existing) ||
      (existing &&
        (existing?.display_label ?? '') === label.displayLabel &&
        (existing?.display_description ?? '') === label.displayDescription)
    )
      continue;

    const isSoftDelete = !label?.displayLabel && !!existing;
    data.push({
      scope_key: scopeKey,
      display_label: isSoftDelete ? existing.display_label ?? scopeKey : label.displayLabel,
      display_description: isSoftDelete ? existing.display_description ?? null : label.displayDescription || null,
      deleted_at: isSoftDelete ? 'now()' : null,
    });
  }

  return data;
};

const buildSubscriptionSetData = (values: PassportFormData, passport?: PassportRecord) => {
  const existingScopesMap = new Map(
    (passport?.passport_subscription_set_scopes ?? []).map((s) => [s.subscription_set_id, s]),
  );
  const currentScopesMap = new Map(
    (values.subscriptionSetMappings ?? [])
      .filter((m) => m.subscriptionSetId)
      .map((m) => [Number(m.subscriptionSetId), m]),
  );

  const scopesData: Record<string, unknown>[] = [];
  const participantsData: Record<string, unknown>[] = [];

  const allSubscriptionSetIds = new Set([...currentScopesMap.keys(), ...existingScopesMap.keys()]);

  for (const subscriptionSetId of allSubscriptionSetIds) {
    const mapping = currentScopesMap.get(subscriptionSetId);
    const existing = existingScopesMap.get(subscriptionSetId);

    // Scope
    const shouldUpsertScope = !mapping || existing?.scope_key !== mapping.scope;
    if (shouldUpsertScope) {
      scopesData.push({
        subscription_set_id: subscriptionSetId,
        scope_key: (!mapping ? existing?.scope_key : mapping?.scope) || 'NONE',
        deleted_at: !mapping ? 'now()' : null,
      });
    }

    // Participants
    const existingParticipants = new Set((existing?.participants ?? []).map((p) => p.participant_org_id));
    const newParticipants = new Set(mapping?.participantOrgIds ?? []);

    for (const orgId of new Set([...existingParticipants, ...newParticipants])) {
      const isAdded = !existingParticipants.has(orgId);
      const isRemoved = !newParticipants.has(orgId);
      if (isAdded || isRemoved) {
        participantsData.push({
          participant_org_id: orgId,
          subscription_set_id: subscriptionSetId,
          deleted_at: isRemoved ? 'now()' : null,
        });
      }
    }
  }

  return { scopesData, participantsData };
};

export function buildUpsertPassportObject(
  values: PassportFormData,
  passport?: PassportRecord,
  authorityOrgId?: string,
) {
  const object: Record<string, unknown> = {
    name: values.name,
    description: values.description || null,
  };

  if (authorityOrgId) {
    object.authority_org_id = authorityOrgId;
  }

  // For edit, include the ID to trigger on_conflict update
  if (passport?.id) {
    object.id = passport.id;
  }

  const scopeLabelsData = buildScopeLabelsData(values, passport);
  if (scopeLabelsData.length > 0) {
    object.passport_scope_labels = {
      data: scopeLabelsData,
      on_conflict: {
        constraint: 'pk_passport_scope_label',
        update_columns: ['display_label', 'display_description', 'deleted_at'],
      },
    };
  }

  const { scopesData, participantsData } = buildSubscriptionSetData(values, passport);

  if (scopesData.length > 0) {
    object.passport_subscription_set_scopes = {
      data: scopesData,
      on_conflict: {
        constraint: 'pk_passport_subscription_set_scope',
        update_columns: ['scope_key', 'deleted_at'],
      },
    };
  }

  if (participantsData.length > 0) {
    object.passport_subscription_set_participants = {
      data: participantsData,
      on_conflict: {
        constraint: 'pk_passport_subscription_set_participant',
        update_columns: ['deleted_at'],
      },
    };
  }

  return object;
}
